import { isWma, wmaDurationSeconds } from './asf'

/** Best-effort media duration for analytics — decodes only the metadata.
 * Resolves null on an unreadable file or after the timeout, so the caller
 * can fire its event either way without blocking anything.
 *
 * WMA never reaches the media element: no browser can open it, so the
 * element would only wait out the timeout to say null. Its header states the
 * duration outright (asf.ts), and a wrong-but-confident null here used to
 * send every long WMA as a single request. */
export function mediaDurationSeconds(
  file: File,
  timeoutMs = 4000,
  signal?: AbortSignal,
): Promise<number | null> {
  if (isWma(file)) {
    if (signal?.aborted) return Promise.resolve(null)
    return wmaDurationSeconds(file).then((d) => (d === null ? null : Math.round(d)))
  }
  return new Promise((resolve) => {
    // Already stopped before this even started: the listener below would
    // never fire, so it would have waited out the whole timeout
    if (signal?.aborted) {
      resolve(null)
      return
    }
    let url: string
    try {
      url = URL.createObjectURL(file)
    } catch {
      resolve(null)
      return
    }
    const audio = document.createElement('audio')
    let settled = false
    const done = (value: number | null) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      URL.revokeObjectURL(url)
      audio.src = ''
      resolve(value)
    }
    const timer = setTimeout(() => done(null), timeoutMs)
    // Asked to stop: give the answer up rather than making them watch out
    // the rest of the timeout
    signal?.addEventListener('abort', () => done(null), { once: true })
    audio.onloadedmetadata = () => {
      const d = audio.duration
      done(Number.isFinite(d) && d > 0 ? Math.round(d) : null)
    }
    audio.onerror = () => done(null)
    audio.preload = 'metadata'
    audio.src = url
  })
}

/** How long the deciding probe waits, against 4 s for the one that only feeds
 * analytics. The person has already pressed Start — a few more seconds once
 * is nothing against sending an eight-hour file as a single request. */
const DECIDING_TIMEOUT_MS = 15_000

/**
 * The duration the chunking decision is made on, which is a different job
 * from the one above.
 *
 * A null there costs an analytics field. A null HERE sends the whole
 * recording as one request, because `needsChunking(null)` is false — and for
 * anything past the threshold that is a guaranteed timeout, reported as the
 * dropped connection this whole feature exists to prevent.
 *
 * Chrome does not load media metadata in a background tab, and choosing a
 * long file and switching away while it uploads is the most ordinary thing
 * in the world. So a first null is not taken as an answer: the probe waits
 * for the tab to come back (the same trick as `afterPaint`, which races a
 * frame against a timer for exactly this reason) and then asks again with
 * room to answer. A file too small to be long is not worth a second ask.
 */
export async function decidingDurationSeconds(
  file: File,
  minBytesWorthRetrying = 0,
  signal?: AbortSignal,
): Promise<number | null> {
  const first = await mediaDurationSeconds(file, undefined, signal)
  // A WMA's answer came from its header, not from a media element that a
  // hidden tab might have starved — waiting for the tab and asking again
  // would only put the same question to the same bytes
  if (first !== null || file.size < minBytesWorthRetrying || isWma(file)) return first
  if (signal?.aborted) return null
  // The signal has to reach all the way down here. This wait is up to 25
  // seconds long, and it sits BEFORE the first place a cancel used to be
  // noticed — so a Cancel pressed during it did stop the upload, but nothing
  // on screen moved for half a minute, which reads as a broken button.
  await tabVisible(undefined, signal)
  if (signal?.aborted) return null
  return mediaDurationSeconds(file, DECIDING_TIMEOUT_MS, signal)
}

/** Resolves when the tab is showing again, or after a ceiling — a tab that
 * never comes back must not hold an upload for ever. */
function tabVisible(ceilingMs = 10_000, signal?: AbortSignal): Promise<void> {
  if (typeof document === 'undefined' || !document.hidden) return Promise.resolve()
  if (signal?.aborted) return Promise.resolve()
  return new Promise((resolve) => {
    const done = () => {
      document.removeEventListener('visibilitychange', onChange)
      signal?.removeEventListener('abort', done)
      clearTimeout(timer)
      resolve()
    }
    const onChange = () => {
      if (!document.hidden) done()
    }
    const timer = setTimeout(done, ceilingMs)
    document.addEventListener('visibilitychange', onChange)
    signal?.addEventListener('abort', done, { once: true })
  })
}
